import React from 'react';
import { SelectOption } from '../../MultiFilterDropdown';
import { File } from 'v2/models';
import { GroupBase, GroupProps } from 'react-select';
import styles from './FolderGroup.module.scss';
import { Tabulation } from './Tabulation';
import classnames from 'classnames';
import { CheckboxDisplay } from 'v2/common/CheckBox/Checkbox';
import folderIcon from 'v2/icons/folder-small.svg';
import chevronDown from 'v2/icons/chevron-down.svg';
import chevronRight from 'v2/icons/chevron-right.svg';
import IconButton from 'v2/common/IconButton/IconButton';
import { useCurrentFileTree } from './CurrentFileTree';
import { IPath, TFolderGroup, join } from './getFileTree';

function getFolderDepth(folder: IPath) {
  if (folder.dirname.length === 0) return 0;
  return folder.dirname.split('/').length;
}

function getAncestorCollapsed(folder: IPath, collapsedFolders: Set<string>) {
  const { dirname } = folder;
  const parts = dirname.split('/');

  let accum = '';
  let i = 0;
  while (accum !== dirname && i < parts.length) {
    accum = join(accum, parts[i]);
    if (collapsedFolders.has(accum)) {
      return true;
    }
    i += 1;
  }
  return false;
}

type IFileGroupProps<
  T extends File,
  TGroup extends GroupBase<SelectOption<T>> = TFolderGroup<T>,
> = GroupProps<SelectOption<T>, true, TGroup>;

export function FolderGroup<T extends File>({
  data: group,
  children,
}: IFileGroupProps<T>) {
  const {
    toggleFolderCollapse,
    handleFolderClick,
    collapsedFolders,
    folderSelectionState,
  } = useCurrentFileTree();
  const { displayPaths: folders } = group || {};

  const handleClickFolder = (e: React.MouseEvent, f: IPath) => {
    const folder = join(f.dirname, f.basename);
    handleFolderClick(folder);
  };

  const handleClickArrow = (e: React.MouseEvent, f: IPath) => {
    const folder = join(f.dirname, f.basename);
    toggleFolderCollapse(folder);
    e.stopPropagation();
  };
  return (
    <div>
      {folders.map((folder) => {
        const depth = getFolderDepth(folder);
        const ancestorCollapsed = getAncestorCollapsed(
          folder,
          collapsedFolders
        );
        const path = join(folder.dirname, folder.basename);
        const collapsed = collapsedFolders.has(path);
        const icon = collapsed ? chevronRight : chevronDown;

        const state = folderSelectionState.get(path);
        const intermediate = state === 'Some';
        const checked = state === 'Every' || state === 'Some';
        return ancestorCollapsed ? null : (
          <div
            className={classnames(styles.row)}
            key={folder.dirname + folder.basename}
            title={folder.basename}
          >
            <CheckboxDisplay
              intermediate={intermediate}
              checked={checked}
              onClick={(e) => handleClickFolder(e, folder)}
            >
              <div
                onClick={(e) => handleClickArrow(e, folder)}
                className={styles.spaceBeforeName}
              >
                <Tabulation count={depth} />
                <IconButton icon={icon} />
                <img src={folderIcon} alt="folder" />
              </div>
              <span className={styles.name}>{folder.basename}</span>
            </CheckboxDisplay>
          </div>
        );
      })}
      {children}
    </div>
  );
}
